import { normalizePoblacionId, poblacionIdsEqual } from "./normalizeLocalizacionIds";

/**
 * Chips favoritos Marina Alta — ids alineados con aux_poblacion / INE.
 * Histórico erróneo: Dénia se guardó como 03070 (Formentera del Segura).
 */
export const POBLACIONES_FAVORITAS_ALICANTE = [
  { id: "03063", nombre: "Dénia" },
  { id: "03095", nombre: "Ondara" },
  { id: "03101", nombre: "Pedreguer" },
  { id: "03908", nombre: "Els Poblets" },
  { id: "03102", nombre: "Pego" },
  { id: "03026", nombre: "Beniarbeig" },
  // Pedanía de Dénia (id local en aux_poblacion).
  { id: "03907", nombre: "La Xara" },
  { id: "03906", nombre: "El Verger" },
];

/** Alias id → id canónico (legado de chips mal mapeados). */
export const POBLACION_ID_ALIASES = {
  "03070": "03063", // chip Dénia antiguo → Dénia real
  "03108": "03095", // Ondara duplicado local
  "03901": "03908", // Els Poblets (INE «Poblets, els»)
  "03138": "03906", // Verger, el (INE) → El Verger
  "99005": "03906", // Vergel, El (legado) → El Verger
  "03905": "03906", // chip El Verger que apuntaba a Benissa
  "03114": "03101", // chip Pedreguer → Romana
  "03115": "03102", // chip Pego → Sagra
  "03031": "03026", // chip Beniarbeig → Benidorm
  "03904": "03908", // chip Els Poblets → San Isidro
};

export const CP_POR_POBLACION = {
  "03063": "03700", // Dénia
  "03070": "03700", // legado chip Dénia
  "03095": "03760", // Ondara
  "03108": "03760",
  "03101": "03720", // Pedreguer
  "03901": "03779", // Els Poblets
  "03908": "03779",
  "03102": "03780", // Pego
  "03026": "03769", // Beniarbeig
  "03907": "03700", // La Xara
  "03138": "03778", // El Verger (INE)
  "99005": "03778", // Vergel, El legado
  "03906": "03778",
  "03902": "03410", // Biar (no usar como La Xara)
  "03091": "03730",
  "03079": "03740",
  "03110": "03791",
  "03113": "03792",
  "03096": "03791",
  "03048": "03769",
  "03139": "03769",
  "03035": "03759",
  "03124": "03769",
  "03090": "03727",
  "03142": "03725",
  "03055": "03710",
  "03018": "03590",
};

/** Nombre de población favorita por ID INE (p. ej. chip El Verger sin guardar aún). */
export function nombrePoblacionPorId(poblacionId) {
  if (!poblacionId) return "";
  const hit = POBLACIONES_FAVORITAS_ALICANTE.find((p) => poblacionIdsEqual(p.id, poblacionId));
  if (hit?.nombre) return hit.nombre;
  const canon = POBLACION_ID_ALIASES[normalizePoblacionId(poblacionId)];
  if (canon) {
    const h2 = POBLACIONES_FAVORITAS_ALICANTE.find((p) => poblacionIdsEqual(p.id, canon));
    if (h2?.nombre) return h2.nombre;
  }
  return "";
}

/**
 * Población para mostrar en UI (tabla, visores, etc.).
 * Con poblacion_id no usa el texto legacy p.poblacion (misma regla que el Visor).
 */
export function nombrePoblacionDisplay(prop = {}, catalog = []) {
  const nombreJoin = prop.poblacion_nombre != null ? String(prop.poblacion_nombre).trim() : "";
  if (nombreJoin) return nombreJoin;

  const id = prop.poblacion_id;
  if (id != null && id !== "") {
    const fromFav = nombrePoblacionPorId(id);
    if (fromFav) return fromFav;

    if (Array.isArray(catalog) && catalog.length) {
      const hit = catalog.find((pb) => poblacionIdsEqual(pb.id, id));
      if (hit?.nombre) return String(hit.nombre).trim();
    }

    return "";
  }

  return String(prop.poblacion || "").trim();
}

export function cpPorPoblacionId(poblacionId) {
  if (!poblacionId) return "";
  const id = normalizePoblacionId(poblacionId);
  if (CP_POR_POBLACION[id]) return CP_POR_POBLACION[id];
  const canon = POBLACION_ID_ALIASES[id];
  return (canon && CP_POR_POBLACION[canon]) || "";
}

/**
 * ¿Este CP está asociado en el mapa a otra población distinta de `poblacionId`?
 * Sirve para no dejar «03700» (Dénia) al elegir Elx/Elche u otro municipio sin CP fijo.
 */
export function cpConflictoConOtraPoblacion(codigoPostal, poblacionId) {
  const cp = String(codigoPostal || "").replace(/\D/g, "").slice(0, 5);
  if (!/^\d{5}$/.test(cp)) return false;
  const idsConEseCp = Object.entries(CP_POR_POBLACION)
    .filter(([, v]) => v === cp)
    .map(([id]) => normalizePoblacionId(id));
  if (!idsConEseCp.length) return false;
  const idSel = normalizePoblacionId(poblacionId);
  const canonSel = POBLACION_ID_ALIASES[idSel] || idSel;
  return !idsConEseCp.some((id) => id === idSel || id === canonSel || POBLACION_ID_ALIASES[id] === canonSel);
}

/** Normaliza nombre de municipio para chips/criterios (El Verger / Vergel, El / … → una sola forma). */
export function canonicalizePoblacionNombre(raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  const k = v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const ALIAS_EL_VERGER = new Set([
    "el verger", "el vergel", "verger", "vergel",
    "verger, el", "vergel, el", "verger el", "vergel el",
  ]);
  if (ALIAS_EL_VERGER.has(k)) return "El Verger";
  return v;
}

/** Lista de poblaciones sin duplicados semánticos (misma localidad, distinto nombre). */
export function dedupePoblacionNombres(list) {
  const seen = new Set();
  const out = [];
  for (const raw of list || []) {
    const canon = canonicalizePoblacionNombre(raw);
    if (!canon) continue;
    const k = canon
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(canon);
  }
  return out;
}
